import {
  Component,
  Renderer2,
  ElementRef,
  OnDestroy,
  OnInit,
  DOCUMENT,
  inject,
} from "@angular/core";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { DomSanitizer } from "@angular/platform-browser";
// Services
import { HelperService } from "src/services/helper.service";
import { LayoutService } from "src/services/layout.service";
import { CmpAuthService, EnvSetupObject } from "icamppro-shared-components";
import { FeatureFlagService } from "@icamppro-customer-portal/shared/cmp-base";
import { CMP_ENV_SETUP } from "./cmp-env-setup.injection-token";

declare type LinkDefinition = {
  charset?: string;
  crossorigin?: string;
  href?: string;
  hreflang?: string;
  media?: string;
  rel?: string;
  rev?: string;
  sizes?: string;
  target?: string;
  type?: string;
} & {
  [prop: string]: string;
};

@Component({
  standalone: false,
  selector: "cmp-root",
  templateUrl: "./app.component.html",
})
export class AppComponent implements OnDestroy, OnInit {
  elRef = inject(ElementRef);
  renderer = inject(Renderer2);
  helperService = inject(HelperService);
  layoutService = inject(LayoutService);
  router = inject(Router);
  sanitizer = inject(DomSanitizer);
  private authService = inject(CmpAuthService);
  cmpEnv = inject<EnvSetupObject>(CMP_ENV_SETUP);
  private document = inject<Document>(DOCUMENT);
  private featureFlags = inject(FeatureFlagService);

  title = "icamppro-camper";
  private changes: MutationObserver | null = null;
  public tenantInfoReady = false;
  public tenantDynamicCssUrlBySlug = "";
  public tenantDynamicCssUrlById = "";

  constructor() {
    const document = this.document;

    this.cmpEnv.getTenantInfo().then((tenantInfo) => {
      const features: string[] | undefined = (
        tenantInfo as typeof tenantInfo & { features: string[] | undefined }
      ).features;
      if (Array.isArray(features)) {
        this.featureFlags.setFeatureFlags(features, true);
      }
    });

    if (document.location) {
      let url = `${document.location.protocol}//${document.location.hostname}/${document.location.search}`;
      url = url.trim();
      if (url.match("utm_source")) {
        this.helperService.originalLocation = url;
        if (this.layoutService.familyId) {
          this.helperService.setUrl(document.location.search);
          this.helperService.setMostRecentUrl();
        } else {
          this.helperService.setUrl(document.location.search);
        }
      } else {
        if (this.layoutService.familyId) {
          this.helperService.getUrl();
        } else {
          this.helperService.setUrl("");
        }
      }
    }

    if (window.location.search) {
      this.helperService.redirectUrl =
        window.location.pathname + window.location.search;
    }

    // lets see if we can get the body element
    let bodyElement: HTMLElement | null = null;

    if (this.elRef.nativeElement instanceof HTMLElement) {
      if (this.elRef.nativeElement.ownerDocument !== null) {
        bodyElement = this.elRef.nativeElement.ownerDocument.body;
      } else if ((this.elRef.nativeElement as unknown) instanceof Document) {
        bodyElement = (this.elRef.nativeElement as unknown as Document).body;
      }
    } else if (this.elRef.nativeElement instanceof Node) {
      if (this.elRef.nativeElement.parentElement !== null) {
        if (this.elRef.nativeElement.parentElement.ownerDocument !== null) {
          bodyElement =
            this.elRef.nativeElement.parentElement.ownerDocument.body;
        } else if (
          (this.elRef.nativeElement.parentElement as unknown) instanceof
          Document
        ) {
          bodyElement = (
            this.elRef.nativeElement.parentElement as unknown as Document
          ).body;
        }
      }
    }

    // here we set the MutationObservable for setting the body class when a modal is opened or closed.
    this.changes = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        if (bodyElement !== null) {
          // the body element should never have this class
          if (bodyElement.classList.contains("modal")) {
            this.renderer.removeClass(bodyElement, "modal");
          }

          if (mutation.type === "attributes") {
            if (
              mutation.attributeName !== "class" ||
              mutation.target.isSameNode(bodyElement)
            ) {
              // we only care about 'class' attribute changes that are not on the body tag
              return;
            }
            if (
              !bodyElement.classList.contains("modal-open") &&
              (mutation.oldValue === null ||
                !(
                  /\b(modal)\b/g.test(mutation.oldValue) &&
                  /\b(show)\b/g.test(mutation.oldValue) &&
                  /\b(modal-open)\b/g.test(mutation.oldValue)
                )) &&
              mutation.target instanceof HTMLElement &&
              mutation.target.classList.contains("modal") &&
              mutation.target.classList.contains("show")
              // mutation.target.classList.contains('modal-show')
            ) {
              // old did not have the classes, but new does ... se we opened a modal.
              // lets add the body class
              this.renderer.addClass(bodyElement, "modal-open");
            } else if (
              bodyElement.classList.contains("modal-open") &&
              mutation.oldValue !== null &&
              /\b(modal)\b/g.test(mutation.oldValue) &&
              /\b(show)\b/g.test(mutation.oldValue) &&
              /\b(modal-open)\b/g.test(mutation.oldValue) &&
              mutation.target instanceof HTMLElement &&
              !(
                mutation.target.classList.contains("modal") &&
                mutation.target.classList.contains("show")
                // mutation.target.classList.contains('modal-show')
              )
            ) {
              // old did have the classes, but new does not ... so we closed a modal.
              // lets remove the body class if no others are open
              if (bodyElement.querySelectorAll(".modal.show").length === 0) {
                // if (bodyElement.querySelectorAll('.modal.show.modal-show').length === 0) {
                this.renderer.removeClass(bodyElement, "modal-open");
              }
            }
          } else if (mutation.type === "childList") {
            // we need to determine if any of the children have the modal classes
            // instead of looking at each node in the added and the removed lists,
            // lets just query to see if we should be open, because this is faster
            // in this situation.
            if (bodyElement.querySelectorAll(".modal.show").length === 0) {
              // if (bodyElement.querySelectorAll('.modal.show.modal-show').length === 0) {
              this.renderer.removeClass(bodyElement, "modal-open");
            } else {
              this.renderer.addClass(bodyElement, "modal-open");
            }
          }
        }
      });
    });

    this.router.events.subscribe(() => {
      this.layoutService.showAlertModal = false;
    });

    // we don't attach this to the bodyElement so that way we don't trigger
    // events for when we add classes to the body inside the event handler.
    if (this.elRef.nativeElement !== null) {
      this.changes.observe(this.elRef.nativeElement, {
        attributeFilter: ["class"],
        attributes: true,
        attributeOldValue: true,
        characterData: false,
        childList: true,
        subtree: true,
      });
    }
  }

  async ngOnInit() {
    const tenantInfo = await this.cmpEnv.getTenantInfo();
    if (!tenantInfo || !tenantInfo.isActive) {
      this.authService.logOut();
      this.router.navigateByUrl("/");
    }
    if (!tenantInfo) {
      return;
    }
    if (!this.tenantInfoReady) {
      // <link *ngIf="tenantInfoReady" type="text/css" rel="stylesheet" [href]='sanitizer.bypassSecurityTrustResourceUrl(tenantDynamicCssUrlBySlug)' />
      // <link *ngIf="tenantInfoReady" type="text/css" rel="stylesheet" [href]='sanitizer.bypassSecurityTrustResourceUrl(tenantDynamicCssUrlById)' />
      try {
        const head = this.document.head;
        if (head === null) {
          throw new Error("<head> not found within DOCUMENT.");
        }

        // tag by tenant slug
        this.tenantDynamicCssUrlBySlug =
          environment.S3DynamicStyles +
          tenantInfo.slug +
          "." +
          environment.envName +
          ".css";
        const tag1: LinkDefinition = {
          type: "text/css",
          rel: "stylesheet",
          href: this.tenantDynamicCssUrlBySlug,
        };
        const link1 = this.renderer.createElement("link");
        this.renderer.listen(
          link1,
          "load",
          () => (this.layoutService.tenantCssLoaded = true),
        );
        this.renderer.listen(
          link1,
          "error",
          () => (this.layoutService.tenantCssLoaded = true),
        );
        Object.keys(tag1).forEach((prop: string) => {
          return this.renderer.setAttribute(link1, prop, tag1[prop]);
        });
        this.renderer.appendChild(head, link1);
      } catch (e) {
        // do nothing
      }
      this.tenantInfoReady = true;
    }
  }

  ngOnDestroy(): void {
    if (this.changes !== null) {
      this.changes.disconnect();
    }
  }
}
